/**
 * Reusable form fields for Organization create/edit modals
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import type { CreateOrganizationRequest, Organization } from '@/types/organizations';
// Organization type used for parentOptions prop

export type OrganizationFormSection = 'basic' | 'additionalInfo' | 'authSettings';

interface OrganizationFormFieldsProps {
  formData: CreateOrganizationRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreateOrganizationRequest>>;
  showParentSelector?: boolean;
  parentOptions?: Organization[];
  parentSearchTerm?: string;
  setParentSearchTerm?: (term: string) => void;
  parentPage?: number;
  parentTotalPages?: number;
  parentTotal?: number;
  loadingParents?: boolean;
  onLoadParentPage?: (page: number) => void;
  isTopNode?: boolean;
  /** Organisations-id vid redigering, så OIDC-returadressen kan visas. */
  organizationId?: string;
  /** Om satt, rendera endast dessa sektioner (för flikar i redigeringsmodal). */
  onlySections?: OrganizationFormSection[];
}

const OrganizationFormFields: React.FC<OrganizationFormFieldsProps> = ({
  formData,
  setFormData,
  showParentSelector = false,
  parentOptions = [],
  parentSearchTerm = '',
  setParentSearchTerm,
  parentPage = 1,
  parentTotalPages = 1,
  parentTotal = 0,
  loadingParents = false,
  onLoadParentPage,
  isTopNode = false,
  organizationId,
  onlySections,
}) => {
  const { t } = useTranslation();
  const canEditPortal = useAuthStore((state) => {
    const flag = state.user?.is_global_admin;
    return flag === true || flag === 1 || flag === '1';
  });
  const show = (section: OrganizationFormSection) =>
    !onlySections || onlySections.includes(section);
  const portalHost = (formData.portal_hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/\.$/, '');
  const portalCallback =
    portalHost && !portalHost.includes('/') && portalHost.includes('.')
      ? organizationId
        ? `https://${portalHost}/api/auth/oidc/${organizationId}/callback`
        : null
      : undefined;

  return (
    <div className="space-y-4">
      {/* Bild 1: Namn, Beskrivning, Toppnod, Status */}
      {show('basic') && (
        <>
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          {t('organizations.organizationName')} *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          placeholder={t('organizations.namePlaceholder')}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          {t('organizations.description')}
        </label>
        <textarea
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          rows={3}
          placeholder={t('organizations.descriptionPlaceholder')}
        />
      </div>

      {/* Parent Selector (for create modal) */}
      {showParentSelector && (
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.parentOrganization')} ({t('common.optional') || 'valfritt'})
          </label>
          
          <input
            type="text"
            value={parentSearchTerm}
            onChange={e => setParentSearchTerm?.(e.target.value)}
            placeholder={t('common.search')}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault mb-2"
          />
          
          <select
            value={formData.parent_id || ''}
            onChange={e =>
              setFormData({
                ...formData,
                parent_id: e.target.value || undefined,
                is_top_node: !e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            disabled={loadingParents}
          >
            <option value="">{t('organizations.noParent')}</option>
            {parentOptions
              .filter(org => 
                !parentSearchTerm || 
                org.name.toLowerCase().includes(parentSearchTerm.toLowerCase())
              )
              .map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
          </select>
          
          {parentTotalPages > 1 && (
            <div className="flex items-center justify-between mt-2">
              <button
                type="button"
                onClick={() => onLoadParentPage?.(parentPage - 1)}
                disabled={parentPage === 1 || loadingParents}
                className="px-3 py-1 text-sm border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-textSecondary">
                {t('common.page') || 'Sida'} {parentPage} / {parentTotalPages} ({parentTotal} {t('common.total') || 'totalt'})
              </span>
              <button
                type="button"
                onClick={() => onLoadParentPage?.(parentPage + 1)}
                disabled={parentPage >= parentTotalPages || loadingParents}
                className="px-3 py-1 text-sm border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Toppnod: kundtoppnod kan ligga under partner (t.ex. Dialect) och ha egna inställningar */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-textDefault">
          <input
            type="checkbox"
            checked={formData.is_top_node || false}
            onChange={e =>
              setFormData({
                ...formData,
                is_top_node: e.target.checked,
              })
            }
            className="w-4 h-4 border border-border rounded bg-bgElevated text-primary focus:ring-primary"
          />
          {t('organizations.isTopNode') || 'Toppnod'}
        </label>
        <p className="text-xs text-textTertiary mt-1 ml-6">{t('organizations.isTopNodeHint')}</p>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          {t('organizations.status')}
        </label>
        <select
          value={formData.status}
          onChange={e =>
            setFormData({
              ...formData,
              status: e.target.value as 'ACTIVE' | 'SUSPENDED' | 'INACTIVE',
            })
          }
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
        >
          <option value="ACTIVE">{t('organizations.active')}</option>
          <option value="SUSPENDED">{t('organizations.suspended')}</option>
          <option value="INACTIVE">{t('organizations.inactive')}</option>
        </select>
      </div>
        </>
      )}

      {/* Bild 2: Ytterligare information */}
      {show('additionalInfo') && (
        <>
      <div className="border-t border-border pt-4 mt-4">
        <h3 className="text-lg font-semibold text-textDefault mb-4">
          {t('organizations.additionalInfo')}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.address')}
          </label>
          <input
            type="text"
            value={formData.address || ''}
            onChange={e => setFormData({ ...formData, address: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.addressPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.postNr')}
          </label>
          <input
            type="text"
            value={formData.postNr || ''}
            onChange={e => setFormData({ ...formData, postNr: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.postNrPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.town')}
          </label>
          <input
            type="text"
            value={formData.town || ''}
            onChange={e => setFormData({ ...formData, town: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.townPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.orgNr')}
          </label>
          <input
            type="text"
            value={formData.orgNr || ''}
            onChange={e => setFormData({ ...formData, orgNr: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.orgNrPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.contact')}
          </label>
          <input
            type="text"
            value={formData.contact || ''}
            onChange={e => setFormData({ ...formData, contact: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.contactPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.contacttel')}
          </label>
          <input
            type="text"
            value={formData.contacttel || ''}
            onChange={e => setFormData({ ...formData, contacttel: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.contacttelPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.email')}
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.emailPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.costNo')}
          </label>
          <input
            type="text"
            value={formData.costNo || ''}
            onChange={e => setFormData({ ...formData, costNo: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('organizations.costNoPlaceholder')}
          />
        </div>
      </div>

      {/* Car Rental Checkbox – endast relevant för toppnod (en kund/undernod är inte ett uthyrningsföretag) */}
      {(isTopNode || formData.is_top_node) && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-textDefault">
            <input
              type="checkbox"
              checked={formData.carRental || false}
              onChange={e => setFormData({ ...formData, carRental: e.target.checked })}
              className="w-4 h-4 border border-border rounded bg-bgElevated"
            />
            {t('organizations.carRental')}
          </label>
        </div>
      )}
        </>
      )}

      {/* Bild 3: Autentiseringsinställningar (for top nodes only) */}
      {show('authSettings') && (isTopNode || formData.is_top_node) && (
        <>
          <div className="border-t border-border pt-4 mt-4">
            <h3 className="text-lg font-semibold text-textDefault mb-4">
              {t('organizations.authSettings')}
            </h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('organizations.authMethod')}
            </label>
          <select
            value={formData.auth_method}
            onChange={e =>
              setFormData({
                ...formData,
                auth_method: e.target.value as 'PASSWORD' | 'TWO_FACTOR' | 'SAML2' | 'OIDC',
              })
            }
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          >
            <option value="PASSWORD">{t('organizations.passwordAuth')}</option>
            <option value="TWO_FACTOR">{t('organizations.twoFactorAuth')}</option>
            <option value="SAML2">{t('organizations.samlAuth')}</option>
            <option value="OIDC">{t('organizations.oidcAuth')}</option>
          </select>
          <p className="mt-1 text-sm text-textTertiary">{t('organizations.authMethodAppliesToUsers')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('organizations.organizationIdentifier')}
            </label>
            <input
              type="text"
              value={formData.organization_identifier || ''}
              onChange={e =>
                setFormData({ ...formData, organization_identifier: e.target.value })
              }
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
              placeholder={t('organizations.identifierPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('organizations.allowedDomains')}
            </label>
            <input
              type="text"
              value={(formData.allowed_domains || []).join(', ')}
              onChange={e =>
                setFormData({
                  ...formData,
                  allowed_domains: e.target.value
                    .split(',')
                    .map(d => d.trim())
                    .filter(d => d),
                })
              }
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
              placeholder={t('organizations.domainsPlaceholder')}
            />
            <p className="text-xs text-textSecondary mt-1">
              {t('organizations.domainsHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('organizations.portalHostname')}
            </label>
            <input
              type="text"
              value={formData.portal_hostname || ''}
              onChange={e => {
                if (!canEditPortal) return;
                setFormData({ ...formData, portal_hostname: e.target.value.trim().toLowerCase() });
              }}
              readOnly={!canEditPortal}
              className={`w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault ${canEditPortal ? '' : 'opacity-80 cursor-default'}`}
              placeholder={canEditPortal ? t('organizations.portalHostnamePlaceholder') : ''}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-textSecondary mt-1">
              {canEditPortal ? t('organizations.portalHostnameHint') : t('organizations.portalHostnameReadOnly')}
            </p>
            <p className="text-xs text-textSecondary mt-2">
              {t('organizations.portalIdpNotice')}
            </p>
            {portalCallback !== undefined && (
              <div className="mt-2 rounded-lg border border-border bg-bgElevated px-3 py-2">
                <p className="text-xs font-medium text-textDefault">
                  {t('organizations.portalOidcCallbackLabel')}
                </p>
                {portalCallback ? (
                  <code className="mt-1 block break-all text-xs text-textDefault">{portalCallback}</code>
                ) : (
                  <p className="mt-1 text-xs text-textSecondary">
                    {t('organizations.portalOidcCallbackPending', { host: portalHost })}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationFormFields;
